import type { ModelUsageDetail } from '../openai-api';
import type { AnthropicCostBucket } from '../anthropic-api';
import type { AnthropicCostSummary, ApiKeyAccumulator } from './types';
import { formatUtcDateKey } from './constants';

export const parseUsdValue = (value: unknown): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : Number.NaN;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().replace(/,/g, '');
    if (!normalized) return Number.NaN;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : Number.NaN;
  }
  return Number.NaN;
};

/**
 * Resolve cost amount from a cost_report result item.
 * The API returns `amount` in lowest currency units (cents for USD).
 * e.g. "123.45" cents = $1.2345
 */
export const resolveCostAmountUsd = (entry: Record<string, unknown>): number => {
  const amountCents = parseUsdValue(entry.amount);
  if (Number.isFinite(amountCents)) {
    return amountCents / 100;
  }
  return Number.NaN;
};

export const pushDailyCost = (
  dayCosts: Map<string, number>,
  timestampValue: unknown,
  amountUsd: number
): boolean => {
  if (!Number.isFinite(amountUsd)) {
    return false;
  }
  if (typeof timestampValue !== 'string') {
    return false;
  }
  const parsedMs = Date.parse(timestampValue);
  if (!Number.isFinite(parsedMs)) {
    return false;
  }
  const dayKey = formatUtcDateKey(new Date(parsedMs));
  dayCosts.set(dayKey, (dayCosts.get(dayKey) ?? 0) + amountUsd);
  return true;
};

export const summarizeAnthropicCosts = (buckets: AnthropicCostBucket[]): AnthropicCostSummary => {
  const dailyCosts = new Map<string, number>();
  let totalCost = 0;
  let bucketCount = 0;

  for (const bucket of buckets) {
    if (!bucket || typeof bucket !== 'object') {
      continue;
    }
    bucketCount += 1;
    const bucketTimestamp = bucket.starting_at;
    const results = Array.isArray(bucket.results) ? bucket.results : [];

    for (const item of results) {
      if (!item || typeof item !== 'object') {
        continue;
      }
      const amountUsd = resolveCostAmountUsd(item as unknown as Record<string, unknown>);
      if (pushDailyCost(dailyCosts, bucketTimestamp, amountUsd)) {
        totalCost += amountUsd;
      }
    }
  }

  return {
    dailyCosts,
    totalCost,
    rows: bucketCount
  };
};

export const scaleCostBreakdowns = (
  scale: number,
  apiKeyMap: Map<string, ApiKeyAccumulator>,
  modelUsageMap: Map<string, ModelUsageDetail>
): void => {
  modelUsageMap.forEach(modelStat => {
    modelStat.cost *= scale;
  });

  apiKeyMap.forEach(apiKeyStat => {
    apiKeyStat.cost *= scale;
    apiKeyStat.cachedCost *= scale;
    apiKeyStat.modelUsage.forEach(modelStat => {
      modelStat.cost *= scale;
    });
    apiKeyStat.modelCosts.forEach((value, modelName) => {
      apiKeyStat.modelCosts.set(modelName, value * scale);
    });
    apiKeyStat.dailyModelCosts.forEach(dayMap => {
      dayMap.forEach((value, modelName) => {
        dayMap.set(modelName, value * scale);
      });
    });
  });
};

export const distributeCostByTokenShare = (
  targetTotalCost: number,
  apiKeyMap: Map<string, ApiKeyAccumulator>,
  modelUsageMap: Map<string, ModelUsageDetail>,
  modelTokenTotals: Map<string, number>
): void => {
  if (!(targetTotalCost > 0)) {
    return;
  }

  const totalTokensAcrossKeys = Array.from(apiKeyMap.values())
    .reduce((sum, stat) => sum + Math.max(0, stat.totalTokens), 0);
  if (!(totalTokensAcrossKeys > 0)) {
    return;
  }

  apiKeyMap.forEach(apiKeyStat => {
    const keyTokens = Math.max(0, apiKeyStat.totalTokens);
    const keyCost = targetTotalCost * (keyTokens / totalTokensAcrossKeys);
    apiKeyStat.cost = keyCost;
    const cacheRatio = apiKeyStat.contextTokens > 0
      ? Math.max(0, Math.min(1, apiKeyStat.cachedTokens / apiKeyStat.contextTokens))
      : 0;
    apiKeyStat.cachedCost = keyCost * cacheRatio;

    const keyModelTokens = Array.from(apiKeyStat.modelTokenSums.values())
      .reduce((sum, value) => sum + Math.max(0, value), 0);
    if (keyModelTokens <= 0) {
      apiKeyStat.modelCosts.forEach((_, modelName) => {
        apiKeyStat.modelCosts.set(modelName, 0);
      });
      apiKeyStat.modelUsage.forEach(modelStat => {
        modelStat.cost = 0;
      });
      return;
    }

    const oldModelCosts = new Map(apiKeyStat.modelCosts);

    apiKeyStat.modelUsage.forEach((modelStat, modelName) => {
      const modelTokens = Math.max(0, apiKeyStat.modelTokenSums.get(modelName) ?? 0);
      const modelCost = keyCost * (modelTokens / keyModelTokens);
      modelStat.cost = modelCost;
      apiKeyStat.modelCosts.set(modelName, modelCost);
    });

    // Scale dailyModelCosts by the same ratio as modelCosts changed
    apiKeyStat.dailyModelCosts.forEach(dayMap => {
      dayMap.forEach((oldValue, modelName) => {
        const oldTotal = oldModelCosts.get(modelName) ?? 0;
        const newTotal = apiKeyStat.modelCosts.get(modelName) ?? 0;
        dayMap.set(modelName, oldTotal > 0 ? oldValue * (newTotal / oldTotal) : 0);
      });
    });
  });

  const totalModelTokens = Array.from(modelTokenTotals.values())
    .reduce((sum, value) => sum + Math.max(0, value), 0);
  if (!(totalModelTokens > 0)) {
    return;
  }

  modelUsageMap.forEach((modelStat, modelName) => {
    const modelTokens = Math.max(0, modelTokenTotals.get(modelName) ?? 0);
    modelStat.cost = targetTotalCost * (modelTokens / totalModelTokens);
  });
};
